import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import useAnalytics from '../../hooks/useAnalytics';

/**
 * PolicyTemplatesTutorial - Feature 031
 * Guided overlay for the Policy Templates Management page (Templates + Variables tabs),
 * mirroring CredentialsTutorial's topic-menu + spotlight pattern. Steps that need the
 * Create Template/Variable form open ask the parent (via `onRequestFormModal`) to force it
 * open for the duration of that section; steps that live on the other tab ask the parent
 * (via `onRequestTab`) to switch tabs first. Purely informational - never mutates data.
 */
const STEPS = [
  {
    id: 'about-templates',
    title: 'What Are Policy Templates?',
    body: 'A template defines which platform APIs/services a credential is allowed to use, and the exact IAM permission policy behind them. They\u2019re what an admin picks from when adding a new credential, or editing an existing one\u2019s Permissions, in Credentials Management \u2014 this screen is where you create and maintain those choices instead of editing raw JSON in Postman (PROC036).',
  },
  {
    id: 'template-create',
    title: 'Create Template',
    body: 'Starts a new template: a description, the services it grants access to, and the permission policy itself.',
    fallbackBody: 'Once you have a template, you\u2019ll see a Create Template button here.',
  },
  {
    id: 'template-services',
    title: 'Services',
    requiresFormModal: true,
    body: 'The platform services this template grants access to (e.g. iot, s3). Options are drawn from services already used by existing templates.',
  },
  {
    id: 'template-permission-json',
    title: 'Permission Policy (JSON)',
    requiresFormModal: true,
    body: 'The actual IAM-style policy: an array of statements, each with the allowed actions and the resources they apply to. Validated for both JSON syntax and structure before you can save.',
  },
  {
    id: 'template-new-variable',
    title: 'Reference a Variable',
    requiresFormModal: true,
    body: 'Need a reusable value inside the policy (e.g. an account ID shared by several templates)? Use a {{NAME}} placeholder \u2014 click this button to create a new one without leaving the form, or click an existing {{NAME}} chip below the JSON field to insert it at the cursor.',
  },
  {
    id: 'template-edit',
    title: 'Edit / View Template',
    body: 'Opens the same form pre-filled with the template\u2019s current values.',
    fallbackBody: 'Once you have a template, you\u2019ll see an edit icon here.',
  },
  {
    id: 'template-delete',
    title: 'Delete Template',
    body: 'Deletes the template. Blocked by the platform if it\u2019s currently assigned to any credential.',
    fallbackBody: 'Once you have a template, you\u2019ll see a delete icon here.',
  },
  {
    id: 'about-variables',
    title: 'What Are Variables?',
    tab: 'variables',
    body: 'Variables are reusable named values (e.g. an account ID or ARN prefix) you can reference inside a template\u2019s permission policy as a {{NAME}} placeholder, so several templates can share one value without repeating it. They\u2019re scoped to the current environment/region.',
  },
  {
    id: 'variable-create',
    title: 'Create Variable',
    tab: 'variables',
    body: 'Defines a new name/value pair. You can also create one directly from a template\u2019s form \u2014 see "Reference a Variable" above.',
    fallbackBody: 'Once you have a variable, you\u2019ll see a Create Variable button here.',
  },
  {
    id: 'variable-name',
    title: 'Name & Value',
    tab: 'variables',
    requiresFormModal: true,
    body: 'The name is what you reference as {{NAME}} in a template\u2019s policy (can\u2019t be changed after creation); the value is substituted in at request time.',
  },
  {
    id: 'variable-edit',
    title: 'Edit Variable',
    tab: 'variables',
    body: 'Updates the variable\u2019s value. The name can\u2019t be changed \u2014 delete and recreate it instead.',
    fallbackBody: 'Once you have a variable, you\u2019ll see an edit icon here.',
  },
  {
    id: 'variable-delete',
    title: 'Delete Variable',
    tab: 'variables',
    body: 'Deletes the variable. Check it isn\u2019t referenced by any template\u2019s policy first \u2014 the platform doesn\u2019t block this automatically.',
    fallbackBody: 'Once you have a variable, you\u2019ll see a delete icon here.',
  },
];

const SECTIONS = [
  {
    id: 'overview',
    title: 'What Is This Screen For?',
    description: 'How Templates and Variables connect to Credentials.',
    stepIds: ['about-templates', 'about-variables'],
  },
  {
    id: 'creating-templates',
    title: 'Creating a Template',
    description: 'Description, services, permission JSON.',
    stepIds: ['template-create', 'template-services', 'template-permission-json', 'template-new-variable'],
  },
  {
    id: 'managing-templates',
    title: 'Editing & Deleting Templates',
    description: 'Update or remove an existing template.',
    stepIds: ['template-edit', 'template-delete'],
  },
  {
    id: 'variables',
    title: 'Managing Variables',
    description: 'Create, edit and delete {{NAME}} placeholders.',
    stepIds: ['variable-create', 'variable-name', 'variable-edit', 'variable-delete'],
  },
];

const PolicyTemplatesTutorial = ({
  isOpen, onClose, canListVariables, onRequestTab, onRequestFormModal, onShowWrittenGuide,
}) => {
  // sectionId === null means the topic menu is showing; otherwise the tour is scoped
  // to that section's steps only, so users never have to click through every topic.
  const [sectionId, setSectionId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const { setAnalytics } = useAnalytics();

  // Hide the Variables topic entirely if the user can't even list them.
  const visibleSections = canListVariables ? SECTIONS : SECTIONS.filter((s) => s.id !== 'variables');
  const activeSection = visibleSections.find((s) => s.id === sectionId) || null;
  const activeSteps = activeSection
    ? activeSection.stepIds.map((id) => STEPS.find((s) => s.id === id)).filter(Boolean)
    : [];
  const step = activeSteps[stepIndex];
  const isLastStep = !!step && stepIndex === activeSteps.length - 1;

  const recomputeTarget = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(`[data-tutorial="${step.id}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      const isOutOfView = rect.top < 0 || rect.bottom > window.innerHeight;
      if (isOutOfView) {
        el.scrollIntoView({ block: 'center' });
        setTargetRect(el.getBoundingClientRect());
      } else {
        setTargetRect(rect);
      }
    } else {
      setTargetRect(null);
    }
  }, [step]);

  // Switch to the tab the step lives on, and ask the parent to force the Create
  // Template/Variable form open for steps that need real fields to point at.
  useEffect(() => {
    if (!isOpen) return;
    if (onRequestTab) onRequestTab(step?.tab || 'templates');
    if (onRequestFormModal) onRequestFormModal(!!step?.requiresFormModal);
  }, [isOpen, step, onRequestTab, onRequestFormModal]);

  useEffect(() => () => {
    if (onRequestFormModal) onRequestFormModal(false);
  }, [onRequestFormModal]);

  // The form's content can change after the target first appears (e.g. once an existing
  // template's details finish loading in edit mode) - keep recomputing for the step's
  // whole duration, not just until the first non-null rect.
  useEffect(() => {
    if (!isOpen || !step?.requiresFormModal) return undefined;
    const interval = setInterval(recomputeTarget, 250);
    return () => clearInterval(interval);
  }, [isOpen, step, recomputeTarget]);

  useEffect(() => {
    if (!isOpen) return undefined;
    recomputeTarget();
    window.addEventListener('resize', recomputeTarget);
    window.addEventListener('scroll', recomputeTarget, true);
    return () => {
      window.removeEventListener('resize', recomputeTarget);
      window.removeEventListener('scroll', recomputeTarget, true);
    };
  }, [isOpen, recomputeTarget]);

  // Reset back to the topic menu whenever the tutorial is (re)opened.
  useEffect(() => {
    if (isOpen) {
      setSectionId(null);
      setStepIndex(0);
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: 'policy_templates-tutorial-start' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelectSection = (id) => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: `policy_templates-tutorial-topic-${id}` },
    });
    setSectionId(id);
    setStepIndex(0);
  };

  const handleBackToTopics = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'policy_templates-tutorial-topics' },
    });
    setSectionId(null);
    setStepIndex(0);
  };

  const handleNext = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'policy_templates-tutorial-next' },
    });
    if (isLastStep) {
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: 'policy_templates-tutorial-finish' },
      });
      handleBackToTopics();
      return;
    }
    setStepIndex((prev) => prev + 1);
  };

  const handleBack = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'policy_templates-tutorial-back' },
    });
    if (stepIndex === 0) {
      handleBackToTopics();
      return;
    }
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSkip = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'policy_templates-tutorial-skip' },
    });
    setSectionId(null);
    setStepIndex(0);
    onClose();
  };

  // Topic menu: pick a section instead of clicking through every step.
  if (!activeSection) {
    return (
      <div className="fixed inset-0 z-50" role="dialog" aria-label="Guided tutorial - choose a topic">
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={handleSkip}
          onKeyDown={(e) => { if (e.key === 'Escape') handleSkip(); }}
          role="button"
          tabIndex={-1}
          aria-label="Close tutorial"
        />
        <div
          className="absolute bg-white rounded-lg shadow-xl w-96 p-5"
          style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
        >
          <div className="flex justify-between items-start mb-1">
            <h3 className="font-semibold text-gray-900 text-lg">What do you need help with?</h3>
            <button onClick={handleSkip} className="text-gray-400 hover:text-gray-600" aria-label="Close tutorial">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">Pick a topic to jump straight to it.</p>
          <div className="space-y-2">
            {visibleSections.map((section) => (
              <button
                key={section.id}
                onClick={() => handleSelectSection(section.id)}
                className="w-full text-left px-3 py-2.5 rounded-md border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50"
              >
                <div className="font-medium text-gray-900 text-sm">{section.title}</div>
                <div className="text-xs text-gray-500">{section.description}</div>
              </button>
            ))}
          </div>
          {onShowWrittenGuide && (
            <button
              onClick={onShowWrittenGuide}
              className="w-full text-center text-xs text-gray-400 hover:text-indigo-600 mt-4"
            >
              Prefer reading? View the written guide instead
            </button>
          )}
        </div>
      </div>
    );
  }

  const showFallback = !targetRect && !!step.fallbackBody;
  const padding = 6;

  // Position the tooltip card near the target (below if there's room, above otherwise),
  // clamped so it never overflows the viewport - or centered if no target (concept steps
  // and empty-list fallbacks).
  let cardStyle = {};
  if (targetRect && !showFallback) {
    const estimatedCardHeight = 280;
    const spaceBelow = window.innerHeight - targetRect.bottom;
    const spaceAbove = targetRect.top;
    let top = spaceBelow >= spaceAbove
      ? targetRect.bottom + padding + 12
      : targetRect.top - padding - 12 - estimatedCardHeight;
    top = Math.min(Math.max(top, 16), window.innerHeight - estimatedCardHeight - 16);
    const left = Math.min(Math.max(targetRect.left, 16), window.innerWidth - 336);
    cardStyle = { top, left };
  }

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-label={`Guided tutorial - ${activeSection.title}`}>
      {/* Dimmed backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleSkip}
        onKeyDown={(e) => { if (e.key === 'Escape') handleSkip(); }}
        role="button"
        tabIndex={-1}
        aria-label="Close tutorial"
      />

      {/* Spotlight highlight box around the real target element */}
      {targetRect && !showFallback && (
        <div
          className="absolute border-2 border-indigo-400 rounded-md pointer-events-none transition-all duration-200"
          style={{
            top: targetRect.top - padding,
            left: targetRect.left - padding,
            width: targetRect.width + padding * 2,
            height: targetRect.height + padding * 2,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
          }}
        />
      )}

      {/* Step card */}
      <div
        className="absolute bg-white rounded-lg shadow-xl w-80 p-4"
        style={targetRect && !showFallback ? cardStyle : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
      >
        <div className="flex items-start justify-between mb-0.5">
          <button
            onClick={handleBackToTopics}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            &larr; Topics
          </button>
          <button onClick={handleSkip} className="text-gray-400 hover:text-gray-600" aria-label="Skip tutorial">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold text-gray-900">{step.title}</h3>
        </div>
        {step.requiresFormModal && (
          <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-2 py-1 mb-2">
            Inside <strong>{step.tab === 'variables' ? 'Create Variable' : 'Create Template'}</strong> {'\u2014'} opened here for you.
          </div>
        )}
        <p className="text-sm text-gray-700 mb-4">
          {showFallback ? step.fallbackBody : step.body}
        </p>
        <div className="space-y-2">
          <span className="text-xs text-gray-400">{activeSection.title} {'\u2022'} {stepIndex + 1} / {activeSteps.length}</span>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={handleBack}
              className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Back
            </button>
            <button
              onClick={handleSkip}
              className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Skip
            </button>
            <button
              onClick={handleNext}
              className="px-3 py-1.5 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
            >
              {isLastStep ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyTemplatesTutorial;
